import React, { FC, useEffect } from 'react';
import { Navigate, Outlet, useMatch, useNavigate } from 'react-router-dom';
import { AppRoutes, generatePath, ParamNames } from '@/routes/appRoutes';
import { browserHistory } from '@/store/store';
import { CompanyPermissionName } from '@/types';
import { useCheckAcceptedCookies } from '@/features/cookies/redux/hooks';
import { useCompanyPermissionsValidator } from '@/store/permissions/useCompanyPermissionsValidator.tsx';
import { CompanyDetailsTabId } from '@/features/company-details/CompanyDetailsTabId';
import { isAuthorized } from './utils.ts';

export const ProtectedRoute: FC<{
  unauthorizedOnly?: boolean;
  redirectPath: (typeof AppRoutes)[keyof typeof AppRoutes];
  name?: string;
}> = ({ unauthorizedOnly, redirectPath }) => {
  const isAuthenticated = isAuthorized();
  const userControlPage = useMatch(AppRoutes.COMPANY_USERS_CONTROL);
  const modulePage = useMatch(AppRoutes.COMPANY_MODULES);
  const navigate = useNavigate();
  const hasPermission = useCompanyPermissionsValidator(true);

  useCheckAcceptedCookies();

  useEffect(() => {
    if (userControlPage) {
      if (!hasPermission(CompanyPermissionName.CompanyViewAllUsers)) {
        navigate(
          generatePath(AppRoutes.COMPANY_DETAILS, {
            companyId: userControlPage.params.companyId,
            [ParamNames.CompanyDetailsSection]: CompanyDetailsTabId.CompanyName,
          })
        );
        console.info('[ProtectedRoute] Redirect due to insufficient rights');
      }
    }
    if (modulePage) {
      if (
        !(
          hasPermission(CompanyPermissionName.CompanyViewAllRoles) &&
          (hasPermission(CompanyPermissionName.CompanyAssignRole) ||
            hasPermission(CompanyPermissionName.CompanyRevokeRole) ||
            hasPermission(CompanyPermissionName.CompanyViewAllUsers) ||
            hasPermission(CompanyPermissionName.ModuleCreateCustomRole))
        )
      ) {
        navigate(
          generatePath(AppRoutes.COMPANY_DETAILS, {
            companyId: modulePage.params.companyId,
            [ParamNames.CompanyDetailsSection]: CompanyDetailsTabId.CompanyName,
          })
        );
        console.info('[ProtectedRoute] Redirect due to insufficient rights');
      }
    }
  }, [hasPermission, modulePage, navigate, userControlPage]);

  const accessAllowed = unauthorizedOnly ? !isAuthenticated : isAuthenticated;
  if (!accessAllowed) {
    console.group?.(
      `[Protected Route handling] forced navigation from ${browserHistory.location.pathname} to ${redirectPath}`
    );
    console.table?.({
      unauthorizedOnly,
      redirectPath,
      isAuthenticated,
      accessAllowed,
    });
    console.groupEnd?.();
  }
  return accessAllowed ? (
    <Outlet />
  ) : (
    <Navigate to={{ pathname: redirectPath, search: window.location.search }} />
  );
};
